/**
 * Resource Control Panel Component
 *
 * Allows managing resources (allocation, availability) for domains.
 * For business domain with companyId: fetches company employees and supports Add Resource.
 * Used in domain detail sections.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Table, InputNumber, Typography, Progress, Tag, Button, Modal, Input, message } from 'antd';
import { UserAddOutlined } from '@ant-design/icons';
import { listCompanyEmployees, createCompanyEmployee } from '../../services/localBridgeSessionApi';
import { isLocalBridgeApiEnabled } from '../../services/bridgeSessionDataAdapter';
import styles from './ResourceControlPanel.module.css';

const { Text } = Typography;

export interface Resource {
  id: string;
  name: string;
  type: string;
  allocated: number;
  available: number;
}

export interface ResourceControlPanelProps {
  /** Domain ID */
  domainId: string;
  /** Resources list */
  resources: Resource[];
  /** Callback when resources change */
  onResourcesChange?: (resources: Resource[]) => void;
  /** Whether controls are disabled */
  disabled?: boolean;
  /** Whether to use compact layout */
  compact?: boolean;
  /** Company ID (for business domain: fetch employees, show Add Resource) */
  companyId?: string | null;
}

export const ResourceControlPanel: React.FC<ResourceControlPanelProps> = ({
  domainId,
  resources: initialResources,
  onResourcesChange,
  disabled = false,
  compact = false,
  companyId,
}) => {
  const [resources, setResources] = useState<Resource[]>(initialResources);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addName, setAddName] = useState('');
  const [addLoading, setAddLoading] = useState(false);

  const fetchEmployees = useCallback(async () => {
    if (!companyId || domainId !== 'business' || !isLocalBridgeApiEnabled()) return;
    const employees = await listCompanyEmployees(companyId);
    const mapped: Resource[] = employees.map((e) => ({
      id: e.id,
      name: e.name,
      type: 'employee',
      allocated: 0,
      available: 100,
    }));
    setResources(mapped);
  }, [companyId, domainId]);

  useEffect(() => {
    if (companyId && domainId === 'business') {
      fetchEmployees();
    } else {
      setResources(initialResources);
    }
  }, [companyId, domainId, initialResources, fetchEmployees]);

  const handleAddResource = async () => {
    if (!companyId || !addName.trim()) return;
    setAddLoading(true);
    try {
      const created = await createCompanyEmployee(companyId, addName.trim());
      if (created) {
        message.success(`Added ${created.name}`);
        setAddModalOpen(false);
        setAddName('');
        await fetchEmployees();
      } else {
        message.error('Failed to add employee');
      }
    } finally {
      setAddLoading(false);
    }
  };

  const handleAllocationChange = (id: string, value: number | null) => {
    if (value === null) return;
    const newResources = resources.map(r =>
      r.id === id ? { ...r, allocated: Math.max(0, Math.min(r.available, value)) } : r
    );
    setResources(newResources);
    onResourcesChange?.(newResources);
  };

  const columns = [
    {
      title: 'Resource',
      key: 'name',
      render: (_: any, record: Resource) => (
        <div>
          <Text strong>{record.name}</Text>
          <br />
          <Tag color="blue" style={{ marginTop: 4 }}>{record.type}</Tag>
        </div>
      ),
    },
    {
      title: 'Allocated',
      key: 'allocated',
      width: 150,
      render: (_: any, record: Resource) => (
        <InputNumber
          value={record.allocated}
          min={0}
          max={record.available}
          onChange={(value) => handleAllocationChange(record.id, value)}
          disabled={disabled}
          className={styles.allocationInput}
        />
      ),
    },
    {
      title: 'Available',
      dataIndex: 'available',
      key: 'available',
      width: 100,
      render: (available: number) => (
        <Text>{available}</Text>
      ),
    },
    {
      title: 'Utilization',
      key: 'utilization',
      width: 200,
      render: (_: any, record: Resource) => {
        const percentage = record.available > 0
          ? Math.round((record.allocated / record.available) * 100)
          : 0;
        return (
          <Progress
            percent={percentage}
            status={percentage > 90 ? 'exception' : percentage > 70 ? 'active' : 'success'}
            strokeColor={percentage > 90 ? '#ef4444' : percentage > 70 ? '#f59e0b' : '#10b981'}
          />
        );
      },
    },
  ];

  const showAddButton = companyId && domainId === 'business' && isLocalBridgeApiEnabled();

  return (
    <div className={`${styles.panel} ${compact ? styles.compact : ''}`}>
      {!compact && (
        <div className={styles.header}>
          <div>
            <Text strong>Resource Management</Text>
            <Text type="secondary" className={styles.subtitle}>
              Adjust resource allocation for this domain
            </Text>
          </div>
          {showAddButton && (
            <Button
              type="primary"
              icon={<UserAddOutlined />}
              onClick={() => setAddModalOpen(true)}
              disabled={disabled}
              className={styles.addButton}
            >
              Add Resource
            </Button>
          )}
        </div>
      )}
      <Table
        dataSource={resources}
        columns={columns}
        rowKey="id"
        pagination={false}
        className={styles.table}
        size={compact ? 'small' : 'middle'}
      />
      <Modal
        title="Add Resource"
        open={addModalOpen}
        onOk={handleAddResource}
        onCancel={() => {
          setAddModalOpen(false);
          setAddName('');
        }}
        confirmLoading={addLoading}
        okText="Add"
      >
        <p>Add an employee to this company. They can then be assigned to bridge card planks.</p>
        <Input
          placeholder="Employee name"
          value={addName}
          onChange={(e) => setAddName(e.target.value)}
          onPressEnter={handleAddResource}
          autoFocus
        />
      </Modal>
    </div>
  );
};

export default ResourceControlPanel;
